import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Sun, Zap, Target, Moon, Check, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { t } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const REWARD_COLORS = {
  giraffe: "bg-amber-100 text-amber-800",
  quick: "bg-teal-100 text-teal-800",
  challenge: "bg-rose-100 text-rose-800",
  reflection: "bg-indigo-100 text-indigo-800",
};

function PracticeCard({ icon: Icon, iconBg, label, reward, done, testId, children }) {
  const { lang } = useLang();
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
      className={`p-6 rounded-2xl border ${done ? "border-primary/40 bg-primary/5" : "border-border bg-card"}`}
      data-testid={testId}
    >
      <div className="flex items-start gap-4">
        <div className={`w-11 h-11 rounded-xl ${iconBg} flex items-center justify-center shrink-0`}>
          <Icon className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-xs uppercase tracking-widest text-accent font-medium">{label}</div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono ${REWARD_COLORS[testId?.replace("practice-", "")]}`}>
              +{reward} XP
            </span>
            {done && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary text-primary-foreground uppercase tracking-widest inline-flex items-center gap-1">
                <Check className="w-3 h-3" /> {t(lang, "done_today")}
              </span>
            )}
          </div>
          <div className="mt-2">{children}</div>
        </div>
      </div>
    </motion.div>
  );
}

export default function DailyPractice() {
  const { lang } = useLang();
  const { refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [quickAnswer, setQuickAnswer] = useState("");
  const [pickedNeeds, setPickedNeeds] = useState([]);
  const [reflectionAnswer, setReflectionAnswer] = useState("");
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    const { data } = await api.get(`/practice/today?language=${lang}`);
    setData(data);
    setQuickAnswer(data?.quick?.answer || "");
    setPickedNeeds(data?.quick?.needs || []);
    setReflectionAnswer(data?.reflection?.answer || "");
  }, [lang]);

  useEffect(() => { load(); }, [load]);

  const complete = async (type, payload = {}) => {
    setBusy((b) => ({ ...b, [type]: true }));
    try {
      const { data: res } = await api.post("/practice/complete", { type, ...payload });
      if (res.reward_earned > 0) {
        toast.success(`+${res.reward_earned} XP`);
      } else if (res.already_done) {
        toast.info(t(lang, "done_today"));
      }
      await load();
      refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t(lang, "error"));
    } finally {
      setBusy((b) => ({ ...b, [type]: false }));
    }
  };

  const toggleNeed = (n) => {
    setPickedNeeds((prev) => prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]);
  };

  if (!data) return null;
  const { giraffe, quick, challenge, reflection } = data;

  return (
    <div className="space-y-4" data-testid="daily-practice">
      <div className="text-xs uppercase tracking-widest text-accent font-medium">
        {t(lang, "practiced_today") || "Today's practice"}
      </div>

      {/* Bonobo insight */}
      <PracticeCard
        icon={Sun}
        iconBg="bg-amber-500/20 text-amber-700"
        label={`🐒 ${t(lang, "giraffe_tip")}`}
        reward={giraffe.reward}
        done={giraffe.done}
        testId="practice-giraffe"
      >
        <p className="font-heading text-lg leading-relaxed font-light">{giraffe.text}</p>
        {!giraffe.done && (
          <Button
            size="sm"
            className="mt-3 rounded-full"
            disabled={busy.giraffe}
            onClick={() => complete("giraffe")}
            data-testid="practice-giraffe-btn"
          >
            {t(lang, "mark_done")} · +{giraffe.reward} XP
          </Button>
        )}
      </PracticeCard>

      {/* Quick exercise */}
      <PracticeCard
        icon={Zap}
        iconBg="bg-teal-500/20 text-teal-700"
        label={`⚡ ${t(lang, "quick_exercise")}`}
        reward={quick.reward}
        done={quick.done}
        testId="practice-quick"
      >
        <p className="text-sm leading-relaxed">{quick.prompt}</p>
        <Textarea
          value={quickAnswer}
          onChange={(e) => setQuickAnswer(e.target.value)}
          placeholder={t(lang, "write_answer")}
          className="mt-3 min-h-[70px]"
          disabled={quick.done}
          data-testid="practice-quick-input"
        />
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
            {t(lang, "needs_at_play")}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {quick.needs_tags.map((n) => {
              const id = typeof n === "string" ? n : n.id;
              const label = typeof n === "string" ? n : (n.label || n.id);
              return (
                <button
                  key={id}
                  disabled={quick.done}
                  onClick={() => toggleNeed(id)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    pickedNeeds.includes(id)
                      ? "bg-accent text-accent-foreground border-accent"
                      : "border-border hover:bg-secondary"
                  } ${quick.done ? "opacity-60 pointer-events-none" : ""}`}
                  data-testid={`need-tag-${id}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
        {!quick.done && (
          <Button
            size="sm"
            className="mt-3 rounded-full"
            disabled={busy.quick || !quickAnswer.trim()}
            onClick={() => complete("quick", { answer: quickAnswer, needs: pickedNeeds })}
            data-testid="practice-quick-btn"
          >
            {t(lang, "save_answer")} · +{quick.reward} XP
          </Button>
        )}
      </PracticeCard>

      {/* Daily challenge */}
      <PracticeCard
        icon={Target}
        iconBg="bg-rose-500/20 text-rose-700"
        label={`🎯 ${t(lang, "daily_challenge")}`}
        reward={challenge.reward}
        done={challenge.done}
        testId="practice-challenge"
      >
        <div className="text-sm leading-relaxed space-y-1">
          <div className="italic text-muted-foreground">{challenge.hint}</div>
          <div className="mt-2 font-mono text-xs bg-secondary/40 p-3 rounded-lg leading-relaxed">
            <div>· {challenge.template.observation}</div>
            <div>· {challenge.template.feeling}</div>
            <div>· {challenge.template.need}</div>
            <div>· {challenge.template.request}</div>
          </div>
        </div>
        {!challenge.done && (
          <Button
            size="sm"
            className="mt-3 rounded-full"
            disabled={busy.challenge}
            onClick={() => complete("challenge")}
            data-testid="practice-challenge-btn"
          >
            {t(lang, "mark_done")} · +{challenge.reward} XP
          </Button>
        )}
      </PracticeCard>

      {/* Evening reflection */}
      <PracticeCard
        icon={reflection.available ? Moon : Lock}
        iconBg={reflection.available ? "bg-indigo-500/20 text-indigo-700" : "bg-muted text-muted-foreground"}
        label={`🌙 ${t(lang, "evening_reflection")}`}
        reward={reflection.reward}
        done={reflection.done}
        testId="practice-reflection"
      >
        {reflection.available ? (
          <>
            <p className="text-sm leading-relaxed">{reflection.prompt}</p>
            <Textarea
              value={reflectionAnswer}
              onChange={(e) => setReflectionAnswer(e.target.value)}
              placeholder={t(lang, "write_answer")}
              className="mt-3 min-h-[80px]"
              disabled={reflection.done}
              data-testid="practice-reflection-input"
            />
            {!reflection.done && (
              <Button
                size="sm"
                className="mt-3 rounded-full"
                disabled={busy.reflection || !reflectionAnswer.trim()}
                onClick={() => complete("reflection", { answer: reflectionAnswer })}
                data-testid="practice-reflection-btn"
              >
                {t(lang, "save_answer")} · +{reflection.reward} XP
              </Button>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">{t(lang, "reflection_locked")}</p>
        )}
      </PracticeCard>
    </div>
  );
}
